import * as THREE from "three";
import { atlasBox, voxelModel, artToVoxels, swordModel } from "./models.js";
import { BLOCKS } from "./blocks.js";
import { ITEMS } from "./crafting.js";
// One arrow pointing along +z (tip at +0.4): wooden shaft, stone tip, white and red fletching.
// Each arrow owns its geometry and materials, so disposeObj frees it after a flight or pickup.
export function arrowModel() {
  const g = new THREE.Group();
  const box = (w, h, d, z, col) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), new THREE.MeshLambertMaterial({ color: col }));
    m.position.z = z;
    g.add(m);
  };
  box(0.05, 0.05, 0.62, 0, "#c99a5e");
  box(0.1, 0.1, 0.14, 0.33, "#9aa2a9");
  box(0.02, 0.17, 0.17, -0.24, "#f4f1e8");
  box(0.17, 0.02, 0.17, -0.24, "#e8574b");
  return g;
}
export function heldItemModel(id, mats) {
  const it = ITEMS[id];
  if (!it) return null;
  if (id === "arrow") {
    // Held and picked-up arrows stand upright like a tool handle.
    const g = new THREE.Group(), a = arrowModel();
    a.rotation.x = -Math.PI / 2;
    g.add(a);
    return g;
  }
  if (it.kind === "block") {
    const m = new THREE.Mesh(
      atlasBox(0.48, 0.48, 0.48, BLOCKS[it.block].tex),
      mats.solid.clone(),
    );
    // clone() copies userData; this material belongs to this item, not the world.
    m.material.userData.shared = false;
    m.position.set(0, 0.02, 0.12);
    m.castShadow = true;
    return m;
  }
  if (it.tool === "sword") {
    const m = swordModel(it.tier);
    m.rotation.set(-0.25, -Math.PI / 2, 0);
    return m;
  }
  if (it.tool === "axe" || it.tool === "pick") {
    const art =
      it.tool === "axe"
        ? [
            "..HHHH.",
            "..HHHHH",
            "..HHHHH",
            "..SHHH.",
            "..S....",
            "..S....",
            "..S....",
            "..S....",
            "..S....",
          ]
        : [
            "HHHHHHH",
            "H..S..H",
            "...S...",
            "...S...",
            "...S...",
            "...S...",
            "...S...",
            "...S...",
            "...S...",
          ];
    const m = voxelModel(
      artToVoxels(art, { H: it.colour, S: "#80502e" }, 0, 1),
      0.07,
      { center: [3, 1, 0] },
    );
    m.rotation.set(-0.25, -Math.PI / 2, 0);
    return m;
  }
  if(['bow','shield','watering_can','lead','shears','saddle','minecart'].includes(id)) {
    const g=new THREE.Group();const box=(w,h,d,x,y,z,col)=>{const m=new THREE.Mesh(new THREE.BoxGeometry(w,h,d),new THREE.MeshLambertMaterial({color:col}));m.position.set(x,y,z);g.add(m);};
    if(id==='bow'){for(let i=0;i<9;i++){const a=(i/8-.5)*Math.PI;box(.09,.13,.08,Math.cos(a)*.24,Math.sin(a)*.45,.05,'#b98b54');}box(.025,.9,.025,0,0,.05,'#eee5c6');}
    else if(id==='shield'){box(.5,.62,.1,0,.1,0,'#b89153');box(.56,.08,.13,0,.37,0,'#b8cbd5');box(.56,.08,.13,0,-.2,0,'#b8cbd5');box(.14,.2,.14,0,.1,.05,'#cbdce5');}
    else if(id==='watering_can'){box(.35,.3,.3,0,0,0,'#7db8c3');box(.13,.1,.35,0,.1,.3,'#cee0da');}
    else {box(.25,.35,.25,0,0,0,it.colour);}
    return g;
  }
  const m = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.21, 0),
    new THREE.MeshLambertMaterial({ color: it.colour }),
  );
  m.position.z = 0.08;
  return m;
}
export function animalModel(kind) {
  const root = new THREE.Group(),
    legs = [];
  const colours = {
    sheep: ["#eee8d9", "#78685a"],
    cow: ["#865736", "#ede2ce"],
    boar: ["#805545", "#54382c"],
  };
  const [body, face] = colours[kind] || ["#fff3dd","#eeba53"];
  const mats = {
    body: new THREE.MeshLambertMaterial({ color: body }),
    face: new THREE.MeshLambertMaterial({ color: face }),
    eye: new THREE.MeshLambertMaterial({ color: "#242737" }),
    horn: new THREE.MeshLambertMaterial({ color: "#fff3cd" }),
  };
  const box = (w, h, d, x, y, z, mat) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mats[mat]);
    m.position.set(x, y, z);
    m.castShadow = true;
    root.add(m);
    return m;
  };
  if(kind==='chicken'){
    box(.6,.6,.7,0,.65,0,'body');box(.35,.4,.35,0,1,.32,'body');box(.23,.12,.2,0,.95,.58,'face');box(.12,.18,.2,0,1.3,.32,'face');for(const x of[-.12,.12]){box(.05,.07,.03,x,1.08,.51,'eye');legs.push(box(.07,.38,.1,x,.2,0,'face'));}const tail=box(.4,.2,.3,0,.85,-.45,'body');return {root,legs,tail,mats};
  }
  box(0.85, 0.68, 1.1, 0, 0.88, 0, "body");
  box(0.53, 0.5, 0.5, 0, 1.03, 0.68, "face");
  box(0.08, 0.1, 0.04, -0.17, 1.13, 0.94, "eye");
  box(0.08, 0.1, 0.04, 0.17, 1.13, 0.94, "eye");
  box(0.38, 0.2, 0.16, 0, 0.94, 0.99, "body");
  for (const x of [-0.3, 0.3])
    for (const z of [-0.38, 0.38]) {
      const leg = box(0.19, 0.53, 0.2, x, 0.3, z, "face");
      legs.push(leg);
    }
  for (const x of [-0.36, 0.36]) box(0.24, 0.13, 0.24, x, 1.2, 0.61, "body");
  if (kind === "cow") {
    for (const x of [-0.23, 0.23]) box(0.1, 0.25, 0.12, x, 1.36, 0.65, "horn");
    box(0.38, 0.04, 0.5, 0.08, 1.235, -0.12, "face");
  }
  if (kind === "boar")
    for (const x of [-0.22, 0.22]) box(0.09, 0.23, 0.1, x, 0.95, 1.02, "horn");
  if (kind === "sheep") box(0.65, 0.16, 0.6, 0, 1.33, 0.5, "body");
  const tail = box(0.12, 0.15, 0.36, 0, 0.96, -0.7, "face");
  return { root, legs, tail, mats };
}
